import { Badge } from '@/components/ui/badge';
import { toTitleCase } from '@/utils/toTitleCase';
import type { TestDefinition } from '@/api/tests';

interface Props {
    test: TestDefinition;
    answers: Record<string, unknown>;
}

const renderValue = (value: unknown, type: string) => {
    if (value === undefined || value === null || value === '') {
        return <span className="text-muted-foreground">-</span>;
    }

    if (Array.isArray(value)) {
        if (value.length === 0) return <span className="text-muted-foreground">-</span>;
        return (
            <div className="flex flex-wrap gap-2">
                {value.map((item, index) => (
                    <Badge
                        key={`${String(item)}-${index}`}
                        variant="secondary"
                        className="px-3 py-1 text-sm"
                    >
                        {String(item)}
                    </Badge>
                ))}
            </div>
        );
    }

    if (type === 'checkbox' || typeof value === 'boolean') {
        return value ? 'Sim' : 'Não';
    }

    return <span>{String(value)}</span>;
};

export default function TestResponseViewer({ test, answers }: Props) {
    const sections = test.sections || [];

    return (
        <div className="space-y-12">
            {sections.map((section) => (
                <div key={section.id} className="space-y-6">
                    <div className="mb-4 border-b border-border pb-2">
                        <h2 className="text-xl font-semibold text-foreground">{section.title}</h2>
                        {section.description && (
                            <p className="mt-1 text-sm text-muted-foreground">
                                {section.description}
                            </p>
                        )}
                    </div>

                    <div className="space-y-8">
                        {section.questions.map((field, index) => {
                            const questionLabel = field.field_label
                                ? field.field_label
                                : toTitleCase(field.field_name.replace(/_/g, ' '));
                            const answer = answers?.[field.field_name];
                            const type = field.field_type?.toLowerCase() || '';

                            return (
                                <div
                                    key={`${field.field_name}-${index}`}
                                    className="border-b border-border pb-6 last:border-0 last:pb-0"
                                >
                                    <h3 className="mb-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
                                        {questionLabel}
                                    </h3>
                                    <div className="text-lg font-semibold leading-relaxed text-foreground">
                                        {renderValue(answer, type)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}
